import type { ProductRelationEdge } from '@api/products';

import type { AsSoldRow, BundleKind, SalesAggregateRow, SkuRow } from './types';

/**
 * Bundle reporting for the Sales page: pure builders over a `splitByBundle` product aggregate.
 *
 * The aggregate returns one row per product per bundle it was sold in (null = sold
 * individually), with revenue already allocated across a bundle's products when the sale was
 * stored. From that:
 *   - By product keeps today's product rows and adds how much sold individually, how many
 *     bundles the product went out in, and one expandable child per bundle.
 *   - As sold regroups the same rows into what the customer bought: products sold on their
 *     own, and each bundle once (its products' units / the recipe quantity).
 * Revenue totals match between the views; unit totals do not, by design.
 */

export const BUNDLE_KIND_LABEL: Record<BundleKind, string> = {
    single: 'Single',
    multipack: 'Multipack',
    bundle: 'Bundle',
};

type BundlePart = {
    productId: string;
    productName: string;
    productSku: string;
    bundleId: string | null;
    bundleName: string;
    bundleSku: string;
    units: number;
    revenue: number;
};

export type BundleRecipe = { kind: BundleKind; quantities: Map<string, number> };

export function toBundleParts(rows: SalesAggregateRow[]): BundlePart[] {
    return rows
        .filter((row) => row.grouping?.PRODUCT?.key)
        .map((row) => {
            const p = row.grouping.PRODUCT!;
            const bundleId = p.bundleProductId ?? null;
            return {
                productId: p.key,
                productName: p.name ?? p.key,
                productSku: p.sku ?? '',
                bundleId,
                bundleName: p.bundleName ?? bundleId ?? '',
                bundleSku: p.bundleSku ?? '',
                units: row.data.quantity || 0,
                revenue: row.data.revenue || 0,
            };
        });
}

export function hasBundleSales(parts: BundlePart[]): boolean {
    return parts.some((part) => part.bundleId !== null && part.units > 0);
}

/**
 * Recipes for the bundles that actually sold. The relation graph also holds customisation and
 * clearance links, so it is narrowed to these ids rather than read whole. A bundle with one
 * product inside is a multipack; with several, a bundle.
 */
export function buildBundleRecipes(
    parts: BundlePart[],
    edges: ProductRelationEdge[],
): Map<string, BundleRecipe> {
    const sold = new Set(parts.map((part) => part.bundleId).filter((id): id is string => id !== null));
    const recipes = new Map<string, BundleRecipe>();
    for (const edge of edges) {
        if (!sold.has(edge.parentId)) continue;
        const recipe = recipes.get(edge.parentId) ?? { kind: 'multipack' as BundleKind, quantities: new Map() };
        recipe.quantities.set(edge.childId, Number(edge.quantity) || 1);
        recipe.kind = recipe.quantities.size > 1 ? 'bundle' : 'multipack';
        recipes.set(edge.parentId, recipe);
    }
    // A bundle that sold but has no recipe left in the catalogue still gets a kind from what
    // was sold inside it.
    for (const id of sold) {
        if (recipes.has(id)) continue;
        const products = new Set(parts.filter((part) => part.bundleId === id).map((part) => part.productId));
        recipes.set(id, { kind: products.size > 1 ? 'bundle' : 'multipack', quantities: new Map() });
    }
    return recipes;
}

/** Adds the sold-individually share, bundle count and per-bundle children to product rows. */
export function attachBundleBreakdown(
    rows: SkuRow[],
    parts: BundlePart[],
    recipes: Map<string, BundleRecipe>,
): SkuRow[] {
    const byProduct = new Map<string, BundlePart[]>();
    for (const part of parts) {
        const list = byProduct.get(part.productId) ?? [];
        list.push(part);
        byProduct.set(part.productId, list);
    }
    return rows.map((row) => {
        const productParts = byProduct.get(row.productId) ?? [];
        const total = productParts.reduce((sum, part) => sum + part.units, 0);
        const individually = productParts.filter((part) => part.bundleId === null);
        const individualUnits = individually.reduce((sum, part) => sum + part.units, 0);
        const bundled = productParts
            .filter((part) => part.bundleId !== null && part.units > 0)
            .sort((a, b) => b.units - a.units);
        const soldIndividuallyPct = total > 0 ? (individualUnits / total) * 100 : 100;
        if (bundled.length === 0) {
            return { ...row, soldIndividuallyPct, bundleCount: 0 };
        }
        const child = (key: string, name: string, sku: string, units: number, revenue: number): SkuRow => ({
            productId: `${row.productId}::${key}`,
            name,
            sku,
            units,
            revenue,
            asp: 0,
            growth: '',
            isBundleChild: true,
        });
        const individualRevenue = individually.reduce((sum, part) => sum + part.revenue, 0);
        const children: SkuRow[] = [
            ...(individualUnits > 0
                ? [child('individually', 'Sold individually', '', individualUnits, individualRevenue)]
                : []),
            ...bundled.map((part) => ({
                ...child(part.bundleId!, `in ${part.bundleName}`, part.bundleSku, part.units, part.revenue),
                bundleKind: recipes.get(part.bundleId!)?.kind ?? 'bundle',
            })),
        ];
        return { ...row, soldIndividuallyPct, bundleCount: bundled.length, bundleChildren: children };
    });
}

/** The As sold rows: each product sold on its own, plus each bundle counted once. */
export function buildAsSoldRows(parts: BundlePart[], recipes: Map<string, BundleRecipe>): AsSoldRow[] {
    const rows = new Map<string, AsSoldRow>();
    const productNames = new Map(parts.map((part) => [part.productId, part.productName]));
    for (const part of parts) {
        if (part.bundleId === null) {
            const row = rows.get(part.productId) ?? {
                id: part.productId,
                name: part.productName,
                sku: part.productSku,
                kind: 'single' as BundleKind,
                unitsSold: 0,
                revenue: 0,
                contains: '',
            };
            row.unitsSold += part.units;
            row.revenue += part.revenue;
            rows.set(part.productId, row);
            continue;
        }
        const recipe = recipes.get(part.bundleId);
        const row = rows.get(part.bundleId) ?? {
            id: part.bundleId,
            name: part.bundleName,
            sku: part.bundleSku,
            kind: recipe?.kind ?? 'bundle',
            unitsSold: 0,
            revenue: 0,
            contains: recipe
                ? [...recipe.quantities].map(([id, qty]) => `${qty} × ${productNames.get(id) ?? id}`).join(' + ')
                : '',
        };
        row.revenue += part.revenue;
        // Every product in a bundle implies the same count of bundles; take the largest so a
        // product filtered out of the request cannot shrink it.
        const perBundle = recipe?.quantities.get(part.productId) ?? 1;
        row.unitsSold = Math.max(row.unitsSold, Math.round(part.units / perBundle));
        rows.set(part.bundleId, row);
    }
    return [...rows.values()];
}
